import rangy from "rangy";
import "rangy/lib/rangy-classapplier";
import "rangy/lib/rangy-textrange";

import {
  isAnnotationApplied,
  isFlashCard,
  isNote,
  getCssClassForColor,
  getTargetNodes,
  addArialLabelToAnnotationNodes,
  isUserSelectNone,
  computeCssSelector,
  getMousePosition,
  handleTouchStart,
  isInBoundingClientRect,
  getFirstNonAnnotatedNode,
  getSelectionRect,
  isSelectionOverlapping,
  getTtsOffsetFromRange,
  hasMathML,
  getMathMLPayload,
  Browser,
  getAnnotatedNode,
  isEqualNumber,
  getHandlerClassName,
  annotationMatchesScreen,
  clearRenderedAnnotationHighlights,
  escapeAttributeValue,
} from "./annotationsUtils";

import {
  applyAnnotationTextAndMathHighlights,
  createAnnotationBookmarkFromRange,
  getConsolidatedSelectedText,
  restoreAnnotationSelection,
  selectionContainsMathSvg,
  setSelectionByCharacterOffsets,
} from "./annotation-selection";

import { IAnnotation } from "./annotationTypes";

import {
  ANNOTATION_PROPS,
  //TOUCH_EVENTS,
  SELECTION_DISABLED_CLASSNAMES,
} from "./annotationConstants";
//import { TouchCoordinates, /*MousePosition */ } from "./annotationTypes";

import { DEVICE_TYPE, type DeviceType } from "../../constants";
import { VIEWER_EXPRESS_CONTAINER_ID } from "../../viewer/constants";

type IAnnotationPopoverData = {
  annotationType: string;
  mousePosition: { positionX: number; positionY: number };
  annotations: IAnnotation[];
};

type tAnnotationCallbackFunctions = {
  AnnotationRequestCallback?: (requestData: IAnnotation) => void;
  AnnotationIconClickCallback?: (data: IAnnotationPopoverData) => void;
};

//let touchStartCoordinates: TouchCoordinates;
let highlightApplier: any;
let flashCardApplier: any;
let annotationData: IAnnotation[] = [];
/** Tracks successfully applied highlights so batch load can retry until DOM exists (e.g. lazy TDX). */
const appliedAnnotationIds = new Set<string>();
let contentObserver: MutationObserver | null = null;
let contentObserverDebounce: ReturnType<typeof setTimeout> | undefined;
let isFlushApplyingAnnotations = false;
/** Reposition margin icons after DOM/layout changes (set once `throttledRepositionIcons` exists). */
let scheduleIconRepositionAfterDomMutation: () => void = () => {};
let expressScrollListenerEl: HTMLElement | null = null;
let expressScrollResizeObserver: ResizeObserver | null = null;
// Set by initializeAnnotationCallbacks;
let annotationCallbackFunctions: tAnnotationCallbackFunctions = {};
let deviceType: DeviceType = DEVICE_TYPE.WEB;
let rootElement: HTMLElement;
let elementId: string;
let previousAnnotationScreenElementId: string | undefined;
let iconsContainer: HTMLElement | null = null;

let attachedListenersDeviceType: DeviceType | null = null;
let resizeListenerAttached = false;
let mobileLayoutListenerAttached = false;
let selectionTimeout: ReturnType<typeof setTimeout> | undefined;
let isExternalLink = false;

/** Viewports at or below this width hide margin icons (phones, tablets, iPad, responsive). */
const MOBILE_LAYOUT_MEDIA_QUERY = "(max-width: 1024px)";

const isMobileLayout = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia(MOBILE_LAYOUT_MEDIA_QUERY).matches;
};

/** Wide desktop web only: show note/flashcard margin icons. */
const shouldShowAnnotationIcons = (): boolean => {
  return deviceType === DEVICE_TYPE.WEB && !isMobileLayout();
};

export const setAnnotationCallbacks = (next: Partial<tAnnotationCallbackFunctions>): void => {
  annotationCallbackFunctions = { ...annotationCallbackFunctions, ...next };
};

const annotationBelongsToCurrentScreen = (annotation: IAnnotation): boolean => {
  return annotationMatchesScreen(annotation, elementId);
};

const annotationQueryRoot = (): ParentNode => {
  return rootElement?.isConnected ? rootElement : document;
};

const querySelectorIn = (scope: ParentNode, selector: string): Element | null => {
  if (!selector) return null;
  try {
    return scope.querySelector(selector);
  } catch {
    return null;
  }
};

/**
 * Resolve the bookmark container inside the current chapter.
 * A structural selector can match the same layout in another chapter; data-id
 * keeps the range on the block the highlight was created in.
 */
const findCommonAncestorNode = (selector: string, dataElementId?: string): Element | null => {
  const scope = annotationQueryRoot();

  if (dataElementId) {
    const blocks = scope.querySelectorAll(`[data-id="${escapeAttributeValue(dataElementId)}"]`);
    for (const block of Array.from(blocks)) {
      if (selector) {
        try {
          if (block.matches(selector)) return block;
        } catch {
          // Selector can be a document path that is not a valid match on this node.
        }
      }
      const inner = querySelectorIn(block, selector);
      if (inner) return inner;
    }
  }

  return querySelectorIn(scope, selector);
};

const selectionIsInsideDataElement = (dataElementId?: string): boolean => {
  if (!dataElementId) return true;
  const scope = annotationQueryRoot();
  let blocks: NodeListOf<Element>;
  try {
    blocks = scope.querySelectorAll(`[data-id="${escapeAttributeValue(dataElementId)}"]`);
  } catch {
    return true;
  }
  // Missing block means this chapter's content is not the highlighted copy.
  if (blocks.length === 0) return false;

  const selection = rangy.getSelection();
  if (!selection.rangeCount) return false;
  const startContainer = selection.getRangeAt(0).startContainer;
  return Array.from(blocks).some(
    (block) => block === startContainer || block.contains(startContainer),
  );
};

const detachAnnotationContentObserver = (): void => {
  contentObserver?.disconnect();
  contentObserver = null;
  if (contentObserverDebounce !== undefined) {
    clearTimeout(contentObserverDebounce);
    contentObserverDebounce = undefined;
  }
};

const flushPendingAnnotations = (): void => {
  for (const annotation of annotationData) {
    if (!annotationBelongsToCurrentScreen(annotation)) continue;
    const id = annotation.annotationId ?? "";
    if (!id || appliedAnnotationIds.has(id)) continue;
    addAnnotation(annotation);
  }
  scheduleIconRepositionAfterDomMutation();
};

const scheduleFlushPendingAnnotations = (): void => {
  if (annotationData.length === 0) return;
  if (isFlushApplyingAnnotations) return;

  const hasPending = annotationData.some((a) => {
    const id = a.annotationId ?? "";
    if (!id || appliedAnnotationIds.has(id)) return false;
    return annotationBelongsToCurrentScreen(a);
  });
  if (!hasPending) return;

  clearTimeout(contentObserverDebounce);
  contentObserverDebounce = setTimeout(() => {
    isFlushApplyingAnnotations = true;
    try {
      flushPendingAnnotations();
    } finally {
      isFlushApplyingAnnotations = false;
    }
  }, 100);
};

const attachAnnotationContentObserver = (): void => {
  detachAnnotationContentObserver();
  if (typeof MutationObserver === "undefined" || !rootElement) return;

  contentObserver = new MutationObserver(() => {
    if (isFlushApplyingAnnotations) return;
    scheduleFlushPendingAnnotations();
    scheduleIconRepositionAfterDomMutation();
  });
  contentObserver.observe(rootElement, { childList: true, subtree: true });
};

const upsertWorkingAnnotation = (incoming: IAnnotation): void => {
  const id = incoming.annotationId ?? "";
  if (!id) return;
  const existingIdx = annotationData.findIndex((item) => item.annotationId === id);
  if (existingIdx >= 0) {
    annotationData[existingIdx] = incoming;
  } else {
    annotationData.push(incoming);
  }
};

// maint entry point for annotation callbacks
export const initializeAnnotationCallbacks = (
  _deviceType: DeviceType,
  _rootElement: HTMLElement,
  _annotationCallbackFunctions: tAnnotationCallbackFunctions,
) => {
  console.log("initializeAnnotationCallbacks", _deviceType, _annotationCallbackFunctions);

  detachExpressScrollLayoutWatchers();

  deviceType = _deviceType;
  rootElement = _rootElement;
  annotationCallbackFunctions = _annotationCallbackFunctions;

  initializeRangy();
  detachAnnotationDomListeners();
  subscribeToAnnotationRequestEvent(_deviceType);

  if (typeof window !== "undefined" && !mobileLayoutListenerAttached) {
    mobileLayoutListenerAttached = true;
    window
      .matchMedia(MOBILE_LAYOUT_MEDIA_QUERY)
      .addEventListener("change", syncAnnotationIconsForLayout);
  }

  attachAnnotationContentObserver();
  attachExpressScrollLayoutWatchers();
};

// call when new screen is being loaded
export const annotationScreen = (_elementId: string) => {
  console.log("annotationScreen", _elementId);
  if (
    previousAnnotationScreenElementId !== undefined &&
    previousAnnotationScreenElementId !== _elementId
  ) {
    annotationData = [];
    appliedAnnotationIds.clear();
    if (contentObserverDebounce !== undefined) {
      clearTimeout(contentObserverDebounce);
      contentObserverDebounce = undefined;
    }
    // Identical chapter content can reuse DOM nodes. Highlight wrappers are not
    // part of the screen model, so remove them before this chapter's own
    // highlights are applied.
    if (rootElement?.isConnected) {
      clearRenderedAnnotationHighlights(rootElement);
    }
  }
  previousAnnotationScreenElementId = _elementId;
  elementId = _elementId;
  if (iconsContainer) {
    iconsContainer.innerHTML = "";
  }
};

const initializeRangy = (): void => {
  console.log("initializeRangy");
  rangy.init();
  highlightApplier = rangy.createClassApplier(ANNOTATION_PROPS.ANNOTATION_HIGHLIGHT_CLASS_NAME, {
    elementTagName: ANNOTATION_PROPS.HIGHLIGHT_TAG_NAME,
    elementProperties: {
      onclick: function (event: MouseEvent) {
        handleAnnotationClick(event);
      },
    },
    normalize: false,
    useExistingElements: false,
  });

  flashCardApplier = rangy.createClassApplier(ANNOTATION_PROPS.ANNOTATION_FLASHCARD_CLASS_NAME, {
    elementTagName: ANNOTATION_PROPS.HIGHLIGHT_TAG_NAME,
    elementProperties: {},
    normalize: false,
    useExistingElements: false,
  });
};

const preventDoubleClickOnAnnotatedNode = (event: MouseEvent): void => {
  if (event.detail > 1 && event.target && getAnnotatedNode(event.target as HTMLElement)) {
    event.preventDefault();
  }
};

const resetExternalLinkOnMouseDown = (): void => {
  if (isExternalLink) {
    isExternalLink = false;
  }
};

const resetExternalLinkOnTouchStart = (): void => {
  isExternalLink = false;
};

const detachAnnotationDomListeners = (): void => {
  if (!rootElement || attachedListenersDeviceType === null) {
    return;
  }

  const previousDeviceType = attachedListenersDeviceType;

  if (previousDeviceType === DEVICE_TYPE.WEB) {
    rootElement.removeEventListener("mousedown", preventDoubleClickOnAnnotatedNode, false);
    rootElement.removeEventListener("pointerup", handlePointerUp);
  }

  if (previousDeviceType === DEVICE_TYPE.IOS || previousDeviceType === DEVICE_TYPE.ANDROID) {
    rootElement.removeEventListener("touchstart", handleTouchStart);
  }

  if (previousDeviceType === DEVICE_TYPE.IOS) {
    rootElement.removeEventListener("touchend", handleTouchEnd);
  }

  if (previousDeviceType === DEVICE_TYPE.ANDROID) {
    document.removeEventListener("selectionchange", handleSelectionChangeEvent);
    rootElement.removeEventListener("contextmenu", handleContextMenuEvent);
    rootElement.removeEventListener("mousedown", resetExternalLinkOnMouseDown);
    rootElement.removeEventListener("touchstart", resetExternalLinkOnTouchStart);
    clearTimeout(selectionTimeout);
    selectionTimeout = undefined;
    isExternalLink = false;
  }

  attachedListenersDeviceType = null;
};

const subscribeToAnnotationRequestEvent = (nextDeviceType: DeviceType) => {
  if (nextDeviceType === DEVICE_TYPE.WEB) {
    rootElement.addEventListener("mousedown", preventDoubleClickOnAnnotatedNode, false);
  }

  if (nextDeviceType === DEVICE_TYPE.IOS || nextDeviceType === DEVICE_TYPE.ANDROID) {
    rootElement.addEventListener("touchstart", handleTouchStart);
  }

  if (nextDeviceType === DEVICE_TYPE.IOS) {
    rootElement.addEventListener("touchend", handleTouchEnd);
  } else if (nextDeviceType === DEVICE_TYPE.ANDROID) {
    // selectionchange is used to handle text selection after long-press in Android WebView
    document.addEventListener("selectionchange", handleSelectionChangeEvent);
    // contextmenu is used to handle text selection in links
    rootElement.addEventListener("contextmenu", handleContextMenuEvent);
    rootElement.addEventListener("mousedown", resetExternalLinkOnMouseDown);
    rootElement.addEventListener("touchstart", resetExternalLinkOnTouchStart);
  } else {
    rootElement.addEventListener("pointerup", handlePointerUp);
  }

  attachedListenersDeviceType = nextDeviceType;
};

const handlePointerUp = (event: MouseEvent): void => {
  console.log("handlePointerUp", event);
  if ((event.target as HTMLElement)?.closest("a[href='#']")) {
    console.log("handlePointerUp a tag found, returning");
    return;
  }
  const selection = rangy.getSelection();
  let hasSelection = "";

  const ranges = selection.saveRanges();
  const isEventOnCurrentSelection = isInBoundingClientRect(event);

  // set timeout to handle safari as pointer up firing before text deselect
  setTimeout(() => {
    hasSelection = selection?.toString().trim();
    if (hasSelection && isEventOnCurrentSelection) {
      selection.restoreRanges(ranges);
    }
    handleTextSelect(event);
  }, 10);
};

// open context menu for edit on click of existing note/highlight
const handleAnnotationClick = (event: MouseEvent): void => {
  const hasSelection = window.getSelection()?.toString().trim();

  if (hasSelection) {
    return;
  }

  const element = event.currentTarget as HTMLElement;

  // if keyterm is annotated then display popover only for keyterm not annotation
  if (
    (event.target as HTMLElement)?.closest("a[href='#']") ||
    (event.target as HTMLElement)?.closest(".spf-inline-reference-link")
  ) {
    return;
  }
  const annotationId = element.getAttribute("data-annotation-id") ?? "";
  const mousePosition = getMousePosition(event, deviceType);
  const nearestDataIdElement = element.parentElement?.closest("[data-id]") as HTMLElement;
  const dataElementId = nearestDataIdElement?.dataset.id;

  // Look up full annotation data from the stored annotations
  const storedAnnotation = annotationData.find((a) => a.annotationId === annotationId);

  const requestData: IAnnotation = {
    ...(storedAnnotation || ({} as IAnnotation)),
    annotationId,
    mousePosition,
    dataElementId: dataElementId ?? storedAnnotation?.dataElementId ?? "",
  };

  // Stop propagation so the window-level click listener in App.tsx
  // does not immediately hide the annotation popup we are about to show.
  event.stopPropagation();

  console.log("annotationCallbackFunctions.AnnotationRequestCallback", requestData);
  annotationCallbackFunctions.AnnotationRequestCallback?.(requestData);
};

// const publishAnnotationRequestEvent = async (requestData: Partial<IAnnotation>): Promise<void> => {
//   requestData.screenId = routeId;

//   CDP.event("annotations.request.event").publish(requestData);
// };

// const getSelectionChangeComputedValues = (): any => {
//   const selection = rootElement.getSelection() as any;
//   if (selection?.rangeCount > 0) {
//     const range = selection.getRangeAt(0);
//     const rect = range.getBoundingClientRect();
//     return {
//       top: rect.top,
//       right: rect.right,
//       bottom: rect.bottom,
//       left: rect.left,
//     };
//   }
// };

// on text selection send data and event to listening client for opening the annotation action popup
const handleTextSelect = (event: MouseEvent): void => {
  console.log("handleTextSelect", event);
  const selection = rangy.getSelection();
  const selectedText = getConsolidatedSelectedText(selection);

  // disable annotation selection if a selection disable class name is present in the closest parent element.
  if (disableAnnotation(selection as unknown as Selection)) {
    console.log("handleTextSelect disableAnnotation, returning");
    return;
  }

  if (selectedText?.length) {
    const range = selection.getRangeAt(0); // selection Range
    // common ancestor container for selection range "start" and range "end"
    const commonAncestor = range.commonAncestorContainer;
    // common ancestor can be text node also in that case find the nearest parent HTML node
    let commonAncestorNode: Node | HTMLElement | null | undefined =
      commonAncestor.nodeType == Node.TEXT_NODE ? commonAncestor.parentNode : commonAncestor;
    // get nearest non annotated path for creating bookmark, to avoid getting incorrect bookmark in case of deletion
    commonAncestorNode = getFirstNonAnnotatedNode(commonAncestorNode as HTMLElement);

    // IS THIS NEEDED?
    // css selector of the common ancestor parent node. This is required to identify the target node and offset on page reload.
    const commonAncestorCssSelector = computeCssSelector(commonAncestorNode as Element);

    // mouse event coordinate relative to outermost container. Required to display annotation popup at event location
    const mousePosition = getMousePosition(event, deviceType);
    // selected bounded rect coordinate relative to outermost container. Required to display annotation popup at event location
    const selectionRect = getSelectionRect(event, deviceType);
    // check if current selection is overlapping with existing selection.
    const isOverlappedSelection = isSelectionOverlapping(range) || false;
    const bookmark = createAnnotationBookmarkFromRange(
      range,
      commonAncestorNode as Node,
      commonAncestorCssSelector,
      selection.isBackward(),
      ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME,
    );

    // Get data-id from the document-start of the selection (not anchor — backward selections
    // put the anchor at the end). Used for TTS seek.
    const rangeStartEl =
      range.startContainer.nodeType === Node.TEXT_NODE
        ? range.startContainer.parentElement
        : (range.startContainer as HTMLElement | null);
    const nearestdataIdElement = rangeStartEl?.closest("[data-id]") as HTMLElement | undefined;
    const dataElementId = nearestdataIdElement?.dataset?.id;

    // TTS offsets must include annotation highlight text (same as mark.js).
    const offsetFromBeginning = nearestdataIdElement
      ? getTtsOffsetFromRange(nearestdataIdElement, range)
      : undefined;

    if (!nearestdataIdElement) {
      console.warn(
        "Cannot find parent element for offset calculation for selected text, so skipping calculations for that",
      );
    }

    if (!elementId) {
      console.warn("elementId not found, returning");
    }
    // data to be sent annotation request on text selection
    const requestData: IAnnotation = {
      selectedText,
      elementId: elementId ?? "",
      screenId: elementId ?? "",
      dataElementId: dataElementId ?? "",
      mousePosition,
      selectionRect,
      isOverlappedSelection,
      bookmark: bookmark as {
        start: number;
        end: number;
        backward: boolean;
        commonAncestorCssSelector: string;
      }, // used for normalizing the offset on page load
      selectedTextOffset: offsetFromBeginning,
      selectedTextLength: selectedText.length,
    };

    if (hasMathML(selection.toHtml())) {
      requestData.highlight = getMathMLPayload(selection);
      if (Browser.isFirefox) {
        setSelectionByCharacterOffsets(
          selection,
          rangy,
          commonAncestorNode as Node,
          bookmark.start,
          bookmark.end,
          bookmark.backward,
          ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME,
        );
        requestData.selectedText = getConsolidatedSelectedText(selection);
      }
    }

    console.log(
      "handleTextSelect calling annotationCallbackFunctions.AnnotationRequestCallback",
      requestData,
      annotationCallbackFunctions.AnnotationRequestCallback,
    );
    annotationCallbackFunctions.AnnotationRequestCallback?.(requestData);
  } else {
    console.log("handleTextSelect no selected text, returning");
  }
};

//used to handle text selection using touch event in iOS
const handleTouchEnd = (event: MouseEvent | TouchEvent): void => {
  setTimeout(() => {
    handleTextSelect(event as MouseEvent);
  }, 100);
};

const disableAnnotation = (selection: Selection | null): boolean => {
  if (!selection?.anchorNode) return false;
  const closestElement =
    selection.anchorNode.nodeType === Node.TEXT_NODE
      ? (selection.anchorNode.parentElement as HTMLElement | null)
      : (selection.anchorNode as HTMLElement | null);

  if (closestElement?.closest(SELECTION_DISABLED_CLASSNAMES)) {
    return true;
  }
  return false;
};

/*
 * used to handle text selection using context menu event in Android.
 * pointer events and touch events won't work on hyperlinks in Android.
 */

const handleSelectionEvent = (event: Event): void => {
  setTimeout(() => {
    handleTextSelect(event as MouseEvent);
  }, 100);
};

const handleSelectionChangeEvent = (event: Event): void => {
  clearTimeout(selectionTimeout);
  selectionTimeout = setTimeout(() => {
    const selection = window.getSelection()?.toString();
    if (selection && !isExternalLink) {
      handleSelectionEvent(event);
      isExternalLink = false;
    }
  }, 500);
};

const handleContextMenuEvent = (event: Event): void => {
  const element = event.target as HTMLElement;
  const anchorTag = element?.closest("a");
  if (anchorTag && anchorTag?.getAttribute("href")) {
    isExternalLink = true;
    const range = document.createRange();
    range.selectNodeContents(event.target as Node);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    handleSelectionEvent(event);
  }
};

const ensureIconsContainer = (): void => {
  if (iconsContainer && iconsContainer.isConnected) return;
  if (deviceType !== DEVICE_TYPE.WEB) return;

  iconsContainer = document.createElement("div");
  iconsContainer.classList.add(ANNOTATION_PROPS.ANNOTATION_ICONS_CONTAINER_CLASS_NAME);
  iconsContainer.style.display = "block";
  rootElement.appendChild(iconsContainer);

  if (!resizeListenerAttached) {
    window.addEventListener("resize", throttledRepositionIcons);
    resizeListenerAttached = true;
  }
};

const calculateIconTopPosition = (annotationTop: number): number => {
  if (!iconsContainer) return annotationTop;
  const children = Array.from(iconsContainer.childNodes);
  for (const iconNode of children) {
    const iconTop = Number((iconNode as HTMLElement).style.top.split("px")[0]);
    if (isEqualNumber(iconTop, annotationTop, 10)) {
      return iconTop;
    }
  }
  return annotationTop;
};

const handleMultipleTypesOnSameLine = (isFlashCardType: boolean, topPosition: string): void => {
  if (!iconsContainer) return;

  const noteIcons = iconsContainer.querySelectorAll(
    `.${ANNOTATION_PROPS.ANNOTATION_NOTE_ICON_HANDLER_CLASS_NAME}[style="top: ${topPosition};"]`,
  );

  const shareableCount = Array.from(noteIcons)
    .map((icon) =>
      icon
        .querySelector(`.${ANNOTATION_PROPS.ANNOTATION_ICON_CLASS_NAME}`)
        ?.classList.contains(ANNOTATION_PROPS.ANNOTATION_SHAREABLE_ICON_CLASS_NAME),
    )
    .filter(Boolean).length;

  noteIcons.forEach((icon, idx) => {
    if (noteIcons.length === 1 || (idx === 0 && noteIcons.length === shareableCount)) {
      icon.classList.remove("multi-note");
    } else {
      icon.classList.add("multi-note");
    }
  });

  const oppositeClassName = getHandlerClassName(!isFlashCardType);
  const oppositeIcons = iconsContainer.querySelectorAll(
    `.${oppositeClassName}[style="top: ${topPosition};"]`,
  );

  if (oppositeIcons.length > 0) {
    iconsContainer
      .querySelectorAll(
        `.${ANNOTATION_PROPS.ANNOTATION_NOTE_ICON_HANDLER_CLASS_NAME}[style="top: ${topPosition};"]`,
      )
      .forEach((icon) => icon.classList.add("move-left"));
  }
};

const onAnnotationIconMouseEnter = (event: MouseEvent): void => {
  const target = event.target as HTMLSpanElement;
  const iconType = target.getAttribute("data-icon-type");
  const annotationPos = target.style.top;
  const icons = document.querySelectorAll(
    `[data-icon-type="${iconType}"][style="top: ${annotationPos};"]`,
  );
  if (icons.length > 1) {
    target.setAttribute("data-annotation-count", icons.length.toString());
  }
};

const onAnnotationIconMouseLeave = (event: MouseEvent): void => {
  (event.target as HTMLSpanElement).removeAttribute("data-annotation-count");
};

const onAnnotationIconClick = (event: MouseEvent): void => {
  const target = (event.target as HTMLSpanElement).closest("[data-icon-type]") as HTMLSpanElement;
  if (!target) return;
  const iconType = target.getAttribute("data-icon-type");
  const annotationPos = target.style.top;
  const icons = document.querySelectorAll(
    `[data-icon-type="${iconType}"][style="top: ${annotationPos};"]`,
  );

  const annotationIdsOnLine = Array.from(icons).map(
    (iconNode) => iconNode.getAttribute("data-annotation-id") ?? "",
  );

  const annotationsOnLine = annotationData.filter((a: IAnnotation) =>
    annotationIdsOnLine.includes(a.annotationId as string),
  );

  const requestData: IAnnotationPopoverData = {
    annotationType: iconType ?? "",
    mousePosition: getMousePosition(event, deviceType),
    annotations: annotationsOnLine,
  };

  event.stopPropagation();
  annotationCallbackFunctions.AnnotationIconClickCallback?.(requestData);
};

const addAnnotationIcon = (annotation: IAnnotation): void => {
  if (!shouldShowAnnotationIcons()) return;
  ensureIconsContainer();
  if (!iconsContainer) return;

  if (iconsContainer.querySelector(`[data-annotation-id="${annotation.annotationId}"]`)) {
    return;
  }

  const isFlashCardAnnotation = isFlashCard(annotation);

  const annotationNode = document.querySelector(
    `[data-annotation-type][data-annotation-id="${annotation.annotationId}"]`,
  );
  if (!annotationNode) return;

  let annotationTop = Math.round(
    annotationNode.getBoundingClientRect().top -
      rootElement.getBoundingClientRect().top +
      rootElement.scrollTop,
  );

  const iconHandlerNode = document.createElement("span");
  iconHandlerNode.className = getHandlerClassName(isFlashCardAnnotation);
  iconHandlerNode.setAttribute("data-annotation-id", annotation.annotationId || "");
  iconHandlerNode.setAttribute(
    "data-icon-type",
    isFlashCardAnnotation
      ? ANNOTATION_PROPS.ANNOTATION_TYPE_FLASHCARD
      : ANNOTATION_PROPS.ANNOTATION_TYPE_NOTE,
  );

  const iconSpan = document.createElement("span");
  iconSpan.className = ANNOTATION_PROPS.ANNOTATION_ICON_CLASS_NAME;
  if (annotation.shareable) {
    iconSpan.classList.add(ANNOTATION_PROPS.ANNOTATION_SHAREABLE_ICON_CLASS_NAME);
  }

  iconHandlerNode.addEventListener("mouseenter", onAnnotationIconMouseEnter);
  iconHandlerNode.addEventListener("mouseleave", onAnnotationIconMouseLeave);
  iconHandlerNode.addEventListener("click", onAnnotationIconClick);
  iconHandlerNode.append(iconSpan);

  annotationTop = calculateIconTopPosition(annotationTop);
  iconHandlerNode.style.top = annotationTop + "px";

  iconsContainer.append(iconHandlerNode);
  handleMultipleTypesOnSameLine(isFlashCardAnnotation, `${annotationTop}px`);
};

const deleteAnnotationIcon = (annotation: IAnnotation): void => {
  if (!iconsContainer) return;
  const isFlashCardAnnotation = isFlashCard(annotation);
  const icon = iconsContainer.querySelector(
    `[data-annotation-id="${annotation.annotationId}"]`,
  ) as HTMLSpanElement;

  if (icon) {
    const linePos = icon.style.top;
    icon.remove();
    handleMultipleTypesOnSameLine(isFlashCardAnnotation, linePos);
  }
};

const editAnnotationIcon = (annotation: IAnnotation): void => {
  if (!iconsContainer) return;
  const icon = iconsContainer.querySelector(
    `[data-annotation-id="${annotation.annotationId}"]`,
  ) as HTMLSpanElement;

  if (icon) {
    const childNode = icon.querySelector(`.${ANNOTATION_PROPS.ANNOTATION_ICON_CLASS_NAME}`);
    if (childNode) {
      if (annotation.shareable) {
        childNode.classList.add(ANNOTATION_PROPS.ANNOTATION_SHAREABLE_ICON_CLASS_NAME);
      } else {
        childNode.classList.remove(ANNOTATION_PROPS.ANNOTATION_SHAREABLE_ICON_CLASS_NAME);
      }
    }
    handleMultipleTypesOnSameLine(isFlashCard(annotation), icon.style.top);
  }
};

const repositionIcons = (): void => {
  if (!iconsContainer) return;
  annotationData
    .filter((a: IAnnotation) => isNote(a) || isFlashCard(a))
    .forEach((annotation: IAnnotation) => {
      const annotationNode = document.querySelector(
        `[data-annotation-type][data-annotation-id="${annotation.annotationId}"]`,
      );
      const icon = iconsContainer!.querySelector(
        `[data-annotation-id="${annotation.annotationId}"]`,
      ) as HTMLElement;

      if (annotationNode && icon) {
        icon.style.top =
          Math.round(
            annotationNode.getBoundingClientRect().top -
              rootElement.getBoundingClientRect().top +
              rootElement.scrollTop,
          ) + "px";
      }
    });

  iconsContainer
    .querySelectorAll(`[data-icon-type="${ANNOTATION_PROPS.ANNOTATION_TYPE_NOTE}"]`)
    .forEach((icon: Element) => {
      icon.classList.remove("move-left");
      handleMultipleTypesOnSameLine(false, (icon as HTMLElement).style.top);
    });
};

let repositionTimeoutHandler: ReturnType<typeof setTimeout>;
const throttledRepositionIcons = (): void => {
  clearTimeout(repositionTimeoutHandler);
  repositionTimeoutHandler = setTimeout(repositionIcons, 100);
};

scheduleIconRepositionAfterDomMutation = (): void => {
  if (!iconsContainer || iconsContainer.childNodes.length === 0) return;
  throttledRepositionIcons();
};

const onExpressScrollOrResizeForIcons = (): void => {
  scheduleIconRepositionAfterDomMutation();
};

const detachExpressScrollLayoutWatchers = (): void => {
  if (expressScrollListenerEl) {
    expressScrollListenerEl.removeEventListener("scroll", onExpressScrollOrResizeForIcons);
    expressScrollListenerEl = null;
  }
  expressScrollResizeObserver?.disconnect();
  expressScrollResizeObserver = null;
};

/**
 * Highlights live inside `#viewer-express-container` (overflow-y: auto); margin icons sit on the
 * outer host. Without listening to the scroller, icon `top` stays stale when the user scrolls or
 * when TDX/async content changes height (plugins-lib uses document + window.scrollY instead).
 */
const attachExpressScrollLayoutWatchers = (): void => {
  detachExpressScrollLayoutWatchers();
  if (typeof window === "undefined" || !rootElement) return;

  const scroller = rootElement.querySelector<HTMLElement>(`#${VIEWER_EXPRESS_CONTAINER_ID}`);
  if (!scroller) return;

  expressScrollListenerEl = scroller;
  scroller.addEventListener("scroll", onExpressScrollOrResizeForIcons, { passive: true });

  if (typeof ResizeObserver !== "undefined") {
    expressScrollResizeObserver = new ResizeObserver(() => onExpressScrollOrResizeForIcons());
    expressScrollResizeObserver.observe(scroller);
  }
};

const syncAnnotationIconsForLayout = (): void => {
  if (deviceType !== DEVICE_TYPE.WEB) return;

  if (shouldShowAnnotationIcons()) {
    annotationData.forEach((a) => {
      if (isNote(a)) {
        getTargetNodes(a).forEach((el) => el.classList.remove("mobile-note"));
      }
    });
    const hasIcons = iconsContainer && iconsContainer.childNodes.length > 0;
    if (hasIcons) {
      repositionIcons();
    } else {
      annotationData
        .filter((a) => isNote(a) || isFlashCard(a))
        .forEach((a) => addAnnotationIcon(a));
    }
  } else {
    if (iconsContainer) {
      iconsContainer.innerHTML = "";
    }
    annotationData.forEach((a) => {
      if (!isNote(a)) return;
      const nodes = getTargetNodes(a);
      if (nodes.length > 0) {
        nodes[nodes.length - 1].classList.add("mobile-note");
      }
    });
  }
};

// handler for add note/highlight response
export const addAnnotation = (responseData: IAnnotation): void => {
  console.log("annotations.ts.addAnnotation", responseData);
  if (!annotationBelongsToCurrentScreen(responseData)) {
    return;
  }
  const annotationId = responseData.annotationId ?? "";
  if (annotationId && appliedAnnotationIds.has(annotationId)) {
    if (isAnnotationApplied(responseData)) return;
    appliedAnnotationIds.delete(annotationId);
  }

  upsertWorkingAnnotation(responseData);

  const isFlashCardAnnotation = isFlashCard(responseData);
  const isNoteAnnotation = isNote(responseData);

  const { start, end, backward, commonAncestorCssSelector } = responseData.bookmark;
  const commonAncestorNode = findCommonAncestorNode(
    commonAncestorCssSelector,
    responseData.dataElementId,
  );
  if (!commonAncestorNode) {
    console.warn("commonAncestorNode not found, returning");
    return;
  }
  const selection = rangy.getSelection();

  const isTextMatches = restoreAnnotationSelection(
    selection,
    rangy,
    { start, end, backward },
    commonAncestorNode,
    responseData.selectedText ?? "",
    ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME,
  );

  console.log("isTextMatches", isTextMatches, responseData.selectedText);
  // apply highlight only if current selected text matches with selected text returned via API
  // and the range sits in the same content block it was created in
  if (isTextMatches && !selectionIsInsideDataElement(responseData.dataElementId)) {
    console.warn(
      "highlight location does not match dataElementId, returning",
      responseData.dataElementId,
    );
    // Stop retrying only once this chapter actually has that content block and
    // the bookmark still landed on a different copy of the text.
    if (responseData.dataElementId && annotationId) {
      const scope = annotationQueryRoot();
      let blockExists = false;
      try {
        blockExists = !!scope.querySelector(
          `[data-id="${escapeAttributeValue(responseData.dataElementId)}"]`,
        );
      } catch {
        blockExists = false;
      }
      if (blockExists) {
        appliedAnnotationIds.add(annotationId);
      }
    }
    selection.removeAllRanges();
    return;
  }
  if (isTextMatches) {
    //console.log("Adding annotation:", responseData);
    const selectionRange = selection.getRangeAt(0).cloneRange();
    const hasCompoundMath =
      selectionContainsMathSvg(selectionRange) || Boolean(responseData.highlight);

    if (isFlashCardAnnotation) {
      flashCardApplier.elementAttributes = {
        tabindex: "0",
        role: "button",
        "data-annotation-id": responseData.annotationId,
        "data-annotation-type": responseData.annotationType || "",
      };
      if (hasCompoundMath) {
        applyAnnotationTextAndMathHighlights({
          rangyApi: rangy,
          container: commonAncestorNode,
          applier: flashCardApplier,
          bookmarkStart: start,
          bookmarkEnd: end,
          selectedText: responseData.selectedText ?? "",
          annotationId: responseData.annotationId ?? "",
          mathMlClassName: ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME,
          selectionRange,
          wrapMathSvgs: (svgNodes) => addSVGAnnotations(svgNodes, responseData),
        });
      } else {
        flashCardApplier.applyToSelection();
      }
    } else {
      // do not create highlight or Note if selection is overlapping
      if (isSelectionOverlapping(selection.getRangeAt(0))) {
        selection.removeAllRanges();
        console.log("node already highlighted. overlapped selection");
        return;
      }
      highlightApplier.elementAttributes = {
        tabindex: "0",
        role: "button",
        "data-annotation-id": responseData.annotationId,
        "data-annotation-type": responseData.annotationType || "",
      };

      highlightApplier.elementProperties.className = getCssClassForColor(responseData.color);

      if (hasCompoundMath) {
        applyAnnotationTextAndMathHighlights({
          rangyApi: rangy,
          container: commonAncestorNode,
          applier: highlightApplier,
          bookmarkStart: start,
          bookmarkEnd: end,
          selectedText: responseData.selectedText ?? "",
          annotationId: responseData.annotationId ?? "",
          mathMlClassName: ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME,
          selectionRange,
          wrapMathSvgs: (svgNodes) => addSVGAnnotations(svgNodes, responseData),
        });
      } else {
        highlightApplier.applyToSelection();
      }

      addArialLabelToAnnotationNodes(responseData);
      // Small triangle for notes on native mobile or narrow web viewport (plugins-lib mobile cohort).
      if (
        (deviceType === DEVICE_TYPE.IOS ||
          deviceType === DEVICE_TYPE.ANDROID ||
          isMobileLayout()) &&
        isNoteAnnotation
      ) {
        const annotationNodes = getTargetNodes(responseData);
        if (annotationNodes.length > 0) {
          const lastAnnotationNode = annotationNodes[annotationNodes.length - 1];
          lastAnnotationNode.classList.add("mobile-note");
        }
      }
    }

    if (annotationId) {
      appliedAnnotationIds.add(annotationId);
    }

    if (
      responseData.scrollIntoView ||
      (deviceType == DEVICE_TYPE.WEB && (isNoteAnnotation || isFlashCardAnnotation))
    ) {
      const timerId = setInterval(() => {
        const annotationNode = document.querySelector(
          `[data-annotation-type][data-annotation-id="${responseData.annotationId}"]`,
        );

        if (annotationNode) {
          if (responseData.scrollIntoView) {
            annotationNode.scrollIntoView({ block: "center", behavior: "smooth" });
          }

          if (deviceType == DEVICE_TYPE.WEB && (isNoteAnnotation || isFlashCardAnnotation)) {
            addAnnotationIcon(responseData);
          }

          clearInterval(timerId);
        }
      }, 50);
    }
  } else {
    console.warn("isTextMatches is false, returning", responseData.selectedText);
  }
  selection.removeAllRanges();
};

// handler to delete an annotation from the DOM and internal data store
export const deleteAnnotation = (responseData: IAnnotation): void => {
  console.log("annotations.ts.deleteAnnotation", responseData);
  const { annotationId = "" } = responseData;
  const targetHighlightedNodes = getTargetNodes(responseData);
  targetHighlightedNodes?.forEach((node: Element) => {
    const childNodes = Array.from(node.childNodes);
    if (childNodes?.length) {
      node.replaceWith(...childNodes);
    }
  });

  if (deviceType === DEVICE_TYPE.WEB && (isNote(responseData) || isFlashCard(responseData))) {
    deleteAnnotationIcon(responseData);
  }

  annotationData = annotationData.filter((item) => item.annotationId !== annotationId);
  appliedAnnotationIds.delete(annotationId);
};

// handler to edit an existing annotation (update color, noteText, etc.)
export const editAnnotation = (updatedAnnotation: IAnnotation): void => {
  console.log("annotations.ts.editAnnotation", updatedAnnotation);

  if (!updatedAnnotation.annotationId) {
    console.warn("editAnnotation: annotationId is required");
    return;
  }

  const existingAnnotation = annotationData.find(
    (a) => a.annotationId === updatedAnnotation.annotationId,
  );

  const mergedAnnotation: IAnnotation = existingAnnotation
    ? { ...existingAnnotation, ...updatedAnnotation }
    : updatedAnnotation;

  // Flashcards are companion annotations. Creating one from a highlight/note must
  // use annotations().add with a new annotationId. In-place Edit would unwrap the
  // highlight DOM (delete + re-add as a non-styled flashcard) and drop the source
  // from notebook stores that key by annotationId.
  if (
    existingAnnotation &&
    !isFlashCard(existingAnnotation) &&
    isFlashCard(mergedAnnotation)
  ) {
    console.warn(
      "editAnnotation: refusing Highlight/Note → Flashcard conversion; call annotations().add with a new annotationId",
      mergedAnnotation.annotationId,
    );
    return;
  }

  deleteAnnotation(mergedAnnotation);
  addAnnotation(mergedAnnotation);

  if (
    deviceType === DEVICE_TYPE.WEB &&
    (isNote(mergedAnnotation) || isFlashCard(mergedAnnotation))
  ) {
    editAnnotationIcon(mergedAnnotation);
  }

  console.log("editAnnotation: updated annotation", mergedAnnotation.annotationId);
};

const addSVGAnnotations = (svgNodes: SVGSVGElement[], responseData: IAnnotation): void => {
  svgNodes?.forEach((svgNode: SVGSVGElement) => {
    if (svgNode && svgNode.isConnected && !isUserSelectNone(svgNode)) {
      const svgAnnotationWrapper = document.createElement(ANNOTATION_PROPS.HIGHLIGHT_TAG_NAME);
      svgAnnotationWrapper.append(svgNode.cloneNode(true));

      svgAnnotationWrapper.classList.add(ANNOTATION_PROPS.ANNOTATION_MATH_ML_CLASS_NAME);

      svgAnnotationWrapper.setAttribute(
        ANNOTATION_PROPS.ANNOTATION_ID_ATTRIBUTE_NAME,
        responseData.annotationId || "",
      );

      svgAnnotationWrapper.setAttribute(
        ANNOTATION_PROPS.ANNOTATION_TYPE_ATTRIBUTE_NAME,
        responseData.annotationType || "",
      );

      if (!isFlashCard(responseData)) {
        svgAnnotationWrapper.classList.add(ANNOTATION_PROPS.ANNOTATION_HIGHLIGHT_CLASS_NAME);
        svgAnnotationWrapper.classList.add(getCssClassForColor(responseData.color));
        svgAnnotationWrapper.addEventListener("click", handleAnnotationClick);
      } else {
        svgAnnotationWrapper.classList.add(ANNOTATION_PROPS.ANNOTATION_FLASHCARD_CLASS_NAME);
      }
      svgNode.replaceWith(svgAnnotationWrapper);
    }
  });
};
